import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Input, Image, Button, Icon, Placeholder, Header, Modal } from 'semantic-ui-react';
import { observer } from 'mobx-react-lite';
import { format, isToday, isYesterday, isThisWeek } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../app/stores/store';
import { Link } from 'react-router-dom';
import { reaction } from 'mobx';

interface ConversationScrollState {
  hasScrolledToBottom: boolean;
  lastMessageCount: number;
  lastScrollTop: number;
}

export default observer(function Messages() {
  const { messageStore } = useStore();
  const { t } = useTranslation();
  const [messageInput, setMessageInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [showConversationList, setShowConversationList] = useState(true);
  const [isScrolling, setIsScrolling] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [conversationUpdateKey, setConversationUpdateKey] = useState(0);

  // ── Delete message state ──────────────────────────────────────────────────
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState<string | null>(null);
  const [deletingMessage, setDeletingMessage] = useState(false);

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLInputElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const conversationScrollStateRef = useRef<Map<string, ConversationScrollState>>(new Map());
  const lastConversationIdRef = useRef<string | null>(null);
  const initialLoadDoneRef = useRef(false);
  const lastMessageCountRef = useRef(0);

  useEffect(() => {
    const checkIfMobile = () => {
      if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current);
      resizeTimeoutRef.current = setTimeout(() => {
        const mobile = window.innerWidth <= 768;
        setIsMobile(mobile);
        if (mobile) setShowConversationList(!messageStore.selectedConversation);
      }, 150);
    };
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => {
      window.removeEventListener('resize', checkIfMobile);
      if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, [messageStore.selectedConversation]);

  useEffect(() => {
    const checkConnectionInterval = setInterval(() => {
      if (messageStore.hubConnection) {
        const state = messageStore.hubConnection.state;
        if (state !== 'Connected') {
          if (state === 'Disconnected') messageStore.createHubConnection();
        }
      }
    }, 10000);
    return () => clearInterval(checkConnectionInterval);
  }, [messageStore]);

  useEffect(() => {
    messageStore.refreshConversations();
    return () => { messageStore.clearMessages(); };
  }, [messageStore]);

  useEffect(() => {
    const disposer = reaction(
      () => messageStore.conversations.map(c => c.unreadCount),
      () => setConversationUpdateKey(prev => prev + 1)
    );
    return () => disposer();
  }, [messageStore]);

  useEffect(() => {
    if (!messageStore.selectedConversationId || messageStore.messages.length === 0) return;

    const conversationId = messageStore.selectedConversationId;
    const conversationKey = conversationId !== '00000000-0000-0000-0000-000000000000'
      ? conversationId
      : `new-${messageStore.selectedConversation?.otherUserId}`;

    let scrollState = conversationScrollStateRef.current.get(conversationKey);
    if (!scrollState) {
      scrollState = { hasScrolledToBottom: false, lastMessageCount: 0, lastScrollTop: 0 };
      conversationScrollStateRef.current.set(conversationKey, scrollState);
    }

    const isNewConversation = lastConversationIdRef.current !== conversationKey;
    const hasNewMessages = messageStore.messages.length > scrollState.lastMessageCount;
    const isInitialLoad = !initialLoadDoneRef.current || scrollState.lastMessageCount === 0;

    if (isNewConversation || isInitialLoad) {
      lastConversationIdRef.current = conversationKey;
      scrollState.hasScrolledToBottom = false;
      scrollState.lastMessageCount = 0;
      conversationScrollStateRef.current.set(conversationKey, scrollState);
      setTimeout(() => {
        scrollToBottom(true);
        if (scrollState) {
          scrollState.hasScrolledToBottom = true;
          conversationScrollStateRef.current.set(conversationKey, scrollState);
        }
        setHasScrolledToBottom(true);
      }, 150);
      initialLoadDoneRef.current = true;
    } else if (hasNewMessages) {
      const isUserAtBottom = scrollState.hasScrolledToBottom;
      const lastMessage = messageStore.messages[messageStore.messages.length - 1];
      const isUserMessage = lastMessage?.isSentByCurrentUser;
      if (isUserAtBottom || isUserMessage || isInitialLoad) {
        setTimeout(() => {
          scrollToBottom(isInitialLoad);
          if (scrollState) {
            scrollState.hasScrolledToBottom = true;
            conversationScrollStateRef.current.set(conversationKey, scrollState);
          }
          setHasScrolledToBottom(true);
        }, 100);
      }
    }

    if (scrollState) {
      scrollState.lastMessageCount = messageStore.messages.length;
      conversationScrollStateRef.current.set(conversationKey, scrollState);
    }
    lastMessageCountRef.current = messageStore.messages.length;
  }, [messageStore.messages, messageStore.selectedConversationId, messageStore.selectedConversation?.otherUserId]);

  useEffect(() => {
    if (isMobile) setShowConversationList(!messageStore.selectedConversation);
  }, [messageStore.selectedConversation, isMobile]);

  useEffect(() => {
    if (!isMobile && messageStore.hubConnection?.state === 'Connected') {
      const timer = setTimeout(() => messageStore.setupDesktopMode(), 1000);
      return () => clearTimeout(timer);
    }
  }, [isMobile, messageStore]);

  useEffect(() => {
    const handleResize = () => {
      const nowDesktop = window.innerWidth > 768;
      if (nowDesktop !== !isMobile) messageStore.checkAndUpdateDesktopMode(nowDesktop);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile, messageStore]);

  const handleScroll = useCallback(() => {
    if (!messagesContainerRef.current || !messageStore.selectedConversationId || isScrolling) return;
    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    const conversationId = messageStore.selectedConversationId;
    const conversationKey = conversationId !== '00000000-0000-0000-0000-000000000000'
      ? conversationId : `new-${messageStore.selectedConversation?.otherUserId}`;
    const scrollState = conversationScrollStateRef.current.get(conversationKey);
    if (!scrollState) return;
    const isAtBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 50;
    const isNearBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 200;
    setHasScrolledToBottom(isAtBottom);
    scrollState.hasScrolledToBottom = isAtBottom;
    scrollState.lastScrollTop = scrollTop;
    conversationScrollStateRef.current.set(conversationKey, scrollState);
    setShowScrollToBottom(!isNearBottom && messageStore.messages.length > 3);
    setIsScrolling(true);
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => setIsScrolling(false), 150);
  }, [messageStore.selectedConversationId, messageStore.selectedConversation?.otherUserId, messageStore.messages.length, isScrolling]);

  const scrollToBottom = (instant = false) => {
    if (!messagesContainerRef.current || !messagesEndRef.current) return;
    setIsScrolling(true);
    if (instant) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      setIsScrolling(false);
      setHasScrolledToBottom(true);
      if (messageStore.selectedConversationId) {
        const conversationKey = messageStore.selectedConversationId !== '00000000-0000-0000-0000-000000000000'
          ? messageStore.selectedConversationId
          : `new-${messageStore.selectedConversation?.otherUserId}`;
        const scrollState = conversationScrollStateRef.current.get(conversationKey);
        if (scrollState) {
          scrollState.hasScrolledToBottom = true;
          conversationScrollStateRef.current.set(conversationKey, scrollState);
        }
      }
    } else {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      setTimeout(() => {
        setIsScrolling(false);
        setHasScrolledToBottom(true);
        if (messageStore.selectedConversationId) {
          const conversationKey = messageStore.selectedConversationId !== '00000000-0000-0000-0000-000000000000'
            ? messageStore.selectedConversationId
            : `new-${messageStore.selectedConversation?.otherUserId}`;
          const scrollState = conversationScrollStateRef.current.get(conversationKey);
          if (scrollState) {
            scrollState.hasScrolledToBottom = true;
            conversationScrollStateRef.current.set(conversationKey, scrollState);
          }
        }
      }, 300);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageInput.trim()) return;
    try {
      const messageToSend = messageInput;
      setMessageInput('');
      setTimeout(() => scrollToBottom(), 50);
      await messageStore.sendMessage(messageToSend);
      if (messageInputRef.current) setTimeout(() => messageInputRef.current?.focus(), 100);
    } catch (error) {
      console.error(t('messages.failedToSend'), error);
      setMessageInput(messageInput);
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!messageToDelete) return;
    setDeletingMessage(true);
    try {
      await messageStore.deleteMessage(messageToDelete);
    } finally {
      setDeletingMessage(false);
      setConfirmDeleteOpen(false);
      setMessageToDelete(null);
    }
  };

  const handleConversationSelect = async (conversationId: string, otherUserId: string) => {
    if (!isMobile) {
      const isSameConversation = conversationId !== '00000000-0000-0000-0000-000000000000'
        ? messageStore.selectedConversation?.id === conversationId
        : (messageStore.selectedConversation?.id === conversationId &&
          messageStore.selectedConversation?.otherUserId === otherUserId);
      if (isSameConversation) return;
    }
    const isSameConversationAfterBack = isMobile &&
      messageStore.selectedConversation?.id === conversationId &&
      messageStore.selectedConversation?.otherUserId === otherUserId &&
      !showConversationList;
    try {
      setMessageInput('');
      const conversationKey = conversationId !== '00000000-0000-0000-0000-000000000000'
        ? conversationId : `new-${otherUserId}`;
      if (isSameConversationAfterBack || isMobile) {
        conversationScrollStateRef.current.delete(conversationKey);
        lastConversationIdRef.current = null;
        initialLoadDoneRef.current = false;
        lastMessageCountRef.current = 0;
        setHasScrolledToBottom(false);
      }
      await messageStore.selectConversation(conversationId, otherUserId);
      if (isMobile) setShowConversationList(false);
      setTimeout(() => messageInputRef.current?.focus(), 100);
    } catch (error) {
      console.error(t('messages.conversationError'), error);
    }
  };

  const handleBackToConversations = () => {
    setShowConversationList(true);
    conversationScrollStateRef.current.clear();
    lastConversationIdRef.current = null;
    initialLoadDoneRef.current = false;
    lastMessageCountRef.current = 0;
    setHasScrolledToBottom(false);
    setShowScrollToBottom(false);
    messageStore.clearMessages();
    setMessageInput('');
  };

  const handleSearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current);
    resizeTimeoutRef.current = setTimeout(async () => {
      if (value.trim()) await messageStore.searchConversations(value);
      else await messageStore.refreshConversations();
    }, 300);
  };

  const formatConversationTime = (date: Date | null) => {
    if (!date) return '';
    try {
      if (isToday(date)) return format(date, 'h:mm a');
      if (isYesterday(date)) return t('messages.yesterday');
      if (isThisWeek(date)) return format(date, 'EEE');
      return format(date, 'MMM d');
    } catch { return ''; }
  };

  const groupMessagesByDate = () => {
    const groups: { [key: string]: typeof messageStore.messages } = {};
    messageStore.messages.forEach(message => {
      try {
        let dateKey = '';
        if (isToday(message.createdAt)) dateKey = t('messages.today');
        else if (isYesterday(message.createdAt)) dateKey = t('messages.yesterday');
        else dateKey = format(message.createdAt, 'MMMM d, yyyy');
        if (!groups[dateKey]) groups[dateKey] = [];
        groups[dateKey].push(message);
      } catch { }
    });
    return groups;
  };

  const getCurrentConversationKey = () => {
    if (!messageStore.selectedConversationId) return null;
    return messageStore.selectedConversationId !== '00000000-0000-0000-0000-000000000000'
      ? messageStore.selectedConversationId
      : `new-${messageStore.selectedConversation?.otherUserId}`;
  };

  // ── Render conversation list ──────────────────────────────────────────────
  const renderConversationList = () => {
    const selectedId = messageStore.selectedConversationId;
    const conversations = messageStore.conversations;

    return (
      <div className="conversation-list-container">
        <div className="conversation-header">
          <Header as='h3'>
            <Icon name='comments' />
            <Header.Content>{t('messages.title')}</Header.Content>
          </Header>
          <Input
            fluid
            icon='search'
            placeholder={t('messages.search')}
            value={searchTerm}
            onChange={handleSearchChange}
            loading={messageStore.loading}
          />
        </div>

        <div className="conversation-scroll-area">
          {messageStore.loading ? (
            <div className="loading-conversations">
              <Placeholder fluid>
                <Placeholder.Header image>
                  <Placeholder.Line />
                  <Placeholder.Line />
                </Placeholder.Header>
              </Placeholder>
              <p>{t('messages.loadingConversations')}</p>
            </div>
          ) : conversations.length === 0 ? (
            <div className="empty-conversations">
              <Icon name='users' size='huge' />
              <p className="empty-title">{t('messages.noFriends')}</p>
              <p className="empty-subtitle">{t('messages.addFriends')}</p>
            </div>
          ) : (
            conversations.map((conversation) => {
              const conversationKey = conversation.id !== '00000000-0000-0000-0000-000000000000'
                ? conversation.id : `new-${conversation.otherUserId}`;
              const isSelected = conversation.id !== '00000000-0000-0000-0000-000000000000'
                ? selectedId === conversation.id
                : (messageStore.selectedConversation?.id === '00000000-0000-0000-0000-000000000000' &&
                  messageStore.selectedConversation?.otherUserId === conversation.otherUserId);

              return (
                <div
                  key={conversationKey}
                  className={`conversation-item ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleConversationSelect(conversation.id, conversation.otherUserId)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ')
                      handleConversationSelect(conversation.id, conversation.otherUserId);
                  }}
                >
                  <div className="avatar-container">
                    <Image src={conversation.otherUserImage || '/assets/user.png'} avatar alt={conversation.otherUserFullName} />
                    {conversation.isOtherUserOnline && <div className="online-indicator" />}
                  </div>
                  <div className="conversation-content">
                    <div className="conversation-name">{conversation.otherUserFullName}</div>
                    <div className="conversation-last-message">
                      {conversation.lastMessage || <span className="start-chat-text">{t('messages.startChat')}</span>}
                    </div>
                  </div>
                  <div className="conversation-meta">
                    {conversation.lastMessageAt && (
                      <div className="conversation-time">{formatConversationTime(conversation.lastMessageAt)}</div>
                    )}
                    {conversation.unreadCount > 0 && (
                      <div className="unread-badge">{conversation.unreadCount > 99 ? '99+' : conversation.unreadCount}</div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  // ── Render chat area ──────────────────────────────────────────────────────
  const renderChatArea = () => {
    if (!messageStore.selectedConversation) {
      return (
        <div className="no-conversation-selected">
          <Icon name='comments outline' size='huge' />
          <p>{t('messages.selectFriend')}</p>
        </div>
      );
    }

    const messageGroups = groupMessagesByDate();
    const currentConversationKey = getCurrentConversationKey();
    const currentScrollState = currentConversationKey
      ? conversationScrollStateRef.current.get(currentConversationKey)
      : null;

    return (
      <div className="chat-area-container">

        {/* ── Header ── */}
        <div className="chat-header">
          <div className="header-left">
            {isMobile && (
              <Button
                icon='arrow left'
                basic
                onClick={handleBackToConversations}
                aria-label={t('messages.back')}
                style={{ marginRight: 8 }}
              />
            )}
            <Image
              src={messageStore.selectedConversation.otherUserImage || '/assets/user.png'}
              avatar
              alt={messageStore.selectedConversation.otherUserFullName}
              style={{ marginRight: 10 }}
            />
            <div>
              <div className="chat-username">{messageStore.selectedConversation.otherUserFullName}</div>
              <div className="chat-status">
                {messageStore.selectedConversation.isOtherUserOnline ? (
                  <><span className="status-indicator online" />{t('messages.activeNow')}</>
                ) : (
                  t('messages.offline')
                )}
              </div>
            </div>
          </div>
          <Button
            icon='user'
            basic
            circular
            as={Link}
            to={`/profiles/${messageStore.selectedConversation.otherUserId}`}
            aria-label={t('messages.viewProfile')}
          />
        </div>

        {/* ── Messages Container ── */}
        <div ref={messagesContainerRef} onScroll={handleScroll} className="messages-container">
          {messageStore.loadingMessages ? (
            <div className="loading-messages">
              <Placeholder fluid><Placeholder.Line /><Placeholder.Line /></Placeholder>
            </div>
          ) : (
            <>
              {messageStore.selectedConversation.gameId && (
                <Link to={`/games/${messageStore.selectedConversation.gameId}`} className="game-link">
                  <div className="game-card">
                    <div className="game-icon"><Icon name='game' size='large' /></div>
                    <div className="game-info">
                      <div className="game-subtitle">{t('messages.messageFromGame')}</div>
                      <div className="game-title">{messageStore.selectedConversation.gameTitle}</div>
                      {messageStore.selectedConversation.gameDate && (
                        <div className="game-date">
                          <Icon name='calendar' size='small' />
                          {format(messageStore.selectedConversation.gameDate, 'PPP p')}
                        </div>
                      )}
                    </div>
                    <Icon name='arrow right' />
                  </div>
                </Link>
              )}

              {Object.keys(messageGroups).length === 0 ? (
                <div className="no-messages">
                  <Icon name='comment outline' size='huge' />
                  <p className="no-messages-title">{t('messages.noMessages')}</p>
                  <p className="no-messages-subtitle">{t('messages.sayHello')}</p>
                </div>
              ) : (
                <>
                  {Object.entries(messageGroups).map(([dateKey, messages]) => (
                    <div key={dateKey} className="message-date-group">
                      <div className="date-divider">{dateKey}</div>
                      {messages.map(message => (
                        <div
                          key={message.id}
                          className={`message-bubble-container ${message.isSentByCurrentUser ? 'sent' : 'received'}`}
                        >
                          {/* Delete button — left of bubble for sent messages */}
                          {message.isSentByCurrentUser && (
                            <button
                              className="delete-message-btn"
                              onClick={() => {
                                setMessageToDelete(message.id);
                                setConfirmDeleteOpen(true);
                              }}
                              title={t('gameDetailedChat.deleteButton')}
                              aria-label={t('gameDetailedChat.deleteButton')}
                            >
                              <Icon name='trash alternate outline' />
                            </button>
                          )}

                          <div className={`message-bubble ${message.isSentByCurrentUser ? 'sent' : 'received'}`}>
                            <div className="message-content">{message.content}</div>
                            <div className="message-meta">
                              {format(message.createdAt, 'h:mm a')}
                              {message.isRead && message.isSentByCurrentUser && (
                                <span className="read-indicator">✓✓</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                  <div ref={messagesEndRef} className="scroll-anchor" />
                </>
              )}
            </>
          )}
        </div>

        {/* ── Scroll to bottom ── */}
        {isMobile && !hasScrolledToBottom && messageStore.messages.length > 5 && (
          <Button
            circular
            icon='arrow down'
            onClick={() => {
              scrollToBottom();
              setHasScrolledToBottom(true);
              setShowScrollToBottom(false);
              if (currentConversationKey && currentScrollState) {
                currentScrollState.hasScrolledToBottom = true;
                conversationScrollStateRef.current.set(currentConversationKey, currentScrollState);
              }
            }}
            className="scroll-to-bottom-button"
            aria-label={t('messages.scrollToBottom')}
          />
        )}

        {/* ── Message Input ── */}
        <div className="message-input-container">
          <form onSubmit={handleSendMessage}>
            <input
              ref={messageInputRef}
              type="text"
              placeholder={t('messages.typeMessage')}
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              className="message-input"
              disabled={messageStore.loadingMessages}
              maxLength={2000}
              aria-label={t('messages.typeMessage')}
            />
            <Button
              primary
              icon='paper plane'
              type='submit'
              disabled={!messageInput.trim() || messageStore.loadingMessages}
              className="send-button"
              aria-label={t('messages.send')}
              loading={messageStore.loadingMessages}
            />
          </form>
        </div>

        {/* ── Delete confirm modal ── */}
        <Modal
          size='mini'
          open={confirmDeleteOpen}
          onClose={() => { setConfirmDeleteOpen(false); setMessageToDelete(null); }}
          dimmer='blurring'
        >
          <Modal.Header style={{ backgroundColor: '#f8f8f9', borderBottom: '1px solid #d4d4d5' }}>
            {t('gameDetailedChat.deleteModal.title')}
          </Modal.Header>
          <Modal.Content>
            <p>{t('gameDetailedChat.deleteModal.message')}</p>
            {deletingMessage && (
              <div style={{ textAlign: 'center', margin: '15px 0' }}>
                <Icon loading name='spinner' />
                <span style={{ marginLeft: 10, color: '#767676' }}>
                  {t('gameDetailedChat.deleteModal.deletingMessage')}
                </span>
              </div>
            )}
          </Modal.Content>
          <Modal.Actions style={{ backgroundColor: '#f9fafb', borderTop: '1px solid #d4d4d5' }}>
            <Button
              basic
              onClick={() => { setConfirmDeleteOpen(false); setMessageToDelete(null); }}
              disabled={deletingMessage}
            >
              {t('gameDetailedChat.deleteModal.cancelButton')}
            </Button>
            <Button
              negative
              icon='trash'
              labelPosition='right'
              content={t('gameDetailedChat.deleteModal.confirmButton')}
              loading={deletingMessage}
              disabled={deletingMessage}
              onClick={handleDeleteConfirmed}
            />
          </Modal.Actions>
        </Modal>
      </div>
    );
  };

  return (
    <Container className="messages-container-wrapper">
      <div className="messages-layout">
        {isMobile ? (
          showConversationList ? renderConversationList() : renderChatArea()
        ) : (
          <>
            <div className="conversation-list-desktop">{renderConversationList()}</div>
            <div className="chat-area-desktop">{renderChatArea()}</div>
          </>
        )}
      </div>

      <style>{`
        .messages-container-wrapper {
          margin-top: 70px;
          margin-bottom: 20px;
          height: calc(100vh - 90px);
        }

        .messages-layout {
          height: 100%;
          background: white;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          overflow: hidden;
          display: flex;
        }

        /* ── Conversation list ── */
        .conversation-list-container {
          height: 100%;
          display: flex;
          flex-direction: column;
          background: white;
          border-right: 1px solid #e0e0e0;
          width: 100%;
        }

        .conversation-header {
          padding: 20px;
          border-bottom: 1px solid #e0e0e0;
          background: #fafafa;
        }

        .conversation-scroll-area { flex: 1; overflow-y: auto; overflow-x: hidden; }
        .loading-conversations { padding: 20px; }

        .empty-conversations {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          color: #999;
          padding: 20px;
          text-align: center;
        }

        .empty-title { font-size: 1.1em; font-weight: bold; margin: 20px 0 10px; }
        .empty-subtitle { font-size: 0.9em; opacity: 0.8; }

        .conversation-item {
          padding: 15px 20px;
          border-bottom: 1px solid #f0f0f0;
          cursor: pointer;
          background: transparent;
          border-left: 3px solid transparent;
          transition: background 0.2s;
          display: flex;
          align-items: center;
          min-width: 0;
          width: 100%;
        }

        .conversation-item:hover { background: #f9f9f9; }
        .conversation-item.selected { background: #e8f4fd; border-left: 3px solid #2185d0; }
        .conversation-item.selected:hover { background: #e8f4fd; }

        .avatar-container { position: relative; margin-right: 12px; flex-shrink: 0; }

        .online-indicator {
          position: absolute;
          bottom: 2px; right: 2px;
          width: 12px; height: 12px;
          background: #21ba45;
          border: 2px solid white;
          border-radius: 50%;
        }

        .conversation-content { flex: 1; min-width: 0; overflow: hidden; margin-right: 10px; }

        .conversation-name {
          font-weight: bold;
          margin-bottom: 3px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .conversation-last-message {
          color: #666;
          font-size: 0.9em;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          width: 100%;
        }

        .start-chat-text { font-style: italic; color: #999; }

        .conversation-meta {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          margin-left: 10px;
          flex-shrink: 0;
          min-width: 60px;
        }

        .conversation-time {
          font-size: 0.85em;
          color: #999;
          margin-bottom: 5px;
          white-space: nowrap;
          text-align: right;
        }

        .unread-badge {
          background: #2185d0;
          color: white;
          border-radius: 10px;
          padding: 2px 7px;
          font-size: 0.8em;
          font-weight: bold;
          min-width: 20px;
          text-align: center;
          flex-shrink: 0;
        }

        /* ── Chat area ── */
        .no-conversation-selected {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          color: #999;
          background: #f9f9f9;
        }

        .chat-area-container {
          height: 100%;
          display: flex;
          flex-direction: column;
          background: white;
          width: 100%;
          position: relative;
        }

        .chat-header {
          padding: 12px 16px;
          border-bottom: 1px solid #e0e0e0;
          background: #fafafa;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-shrink: 0;
          min-height: 64px;
        }

        .header-left { display: flex; align-items: center; }
        .chat-username { font-weight: bold; font-size: 1.1em; }

        .chat-status {
          font-size: 0.9em;
          color: #666;
          display: flex;
          align-items: center;
        }

        .status-indicator {
          width: 8px; height: 8px;
          border-radius: 50%;
          display: inline-block;
          margin-right: 5px;
        }

        .status-indicator.online { background: #21ba45; }

        .messages-container {
          flex: 1;
          overflow-y: auto;
          padding: 16px;
          background: #f9f9f9;
          position: relative;
        }

        .loading-messages { padding: 20px; }
        .game-link { text-decoration: none; display: block; margin-bottom: 20px; }

        .game-card {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: 2px solid #667eea;
          border-radius: 12px;
          padding: 15px 20px;
          color: white;
          cursor: pointer;
          transition: all 0.3s;
          box-shadow: 0 4px 6px rgba(102, 126, 234, 0.3);
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .game-card:hover { transform: translateY(-2px); box-shadow: 0 6px 12px rgba(102, 126, 234, 0.4); }

        .game-icon {
          background: rgba(255,255,255,0.2);
          border-radius: 50%;
          width: 40px; height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .game-info { flex: 1; }
        .game-subtitle { font-size: 0.75em; opacity: 0.9; margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.5px; }
        .game-title { font-weight: bold; font-size: 1.1em; }
        .game-date { font-size: 0.85em; opacity: 0.9; margin-top: 4px; display: flex; align-items: center; }

        .no-messages {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          color: #999;
        }

        .no-messages-title { font-size: 1.1em; font-weight: bold; margin: 20px 0 10px; }
        .no-messages-subtitle { font-size: 0.9em; opacity: 0.8; }
        .message-date-group { margin-bottom: 20px; }

        .date-divider {
          text-align: center;
          margin: 20px 0;
          color: #999;
          font-size: 0.85em;
          position: relative;
        }

        .date-divider:before, .date-divider:after {
          content: '';
          position: absolute;
          top: 50%;
          width: 40%;
          height: 1px;
          background: #e0e0e0;
        }

        .date-divider:before { left: 0; }
        .date-divider:after { right: 0; }

        /* ── Message bubble ── */
        .message-bubble-container {
          margin-bottom: 12px;
          display: flex;
          align-items: flex-end;
          gap: 6px;
        }

        .message-bubble-container.sent { justify-content: flex-end; }
        .message-bubble-container.received { justify-content: flex-start; }

        .message-bubble {
          max-width: ${isMobile ? '80%' : '60%'};
          padding: 10px 14px;
          border-radius: 18px;
          word-break: break-word;
          overflow-wrap: break-word;
          font-size: ${isMobile ? '0.95em' : '1em'};
          line-height: 1.4;
        }

        .message-bubble.sent { background: #2185d0; color: white; }
        .message-bubble.received { background: white; color: black; border: 1px solid #e0e0e0; }
        .message-content { word-wrap: break-word; }

        .message-meta {
          font-size: 0.75em;
          margin-top: 4px;
          opacity: 0.7;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .message-bubble.sent .message-meta { justify-content: flex-end; }
        .message-bubble.received .message-meta { justify-content: flex-start; }
        .read-indicator { margin-left: 2px; }

        /* ── Delete button — sits beside the bubble ── */
        .delete-message-btn {
          background: none;
          border: none;
          cursor: pointer;
          color: #bbb;
          padding: 4px 6px;
          font-size: 1.15em;
          line-height: 1;
          display: inline-flex;
          align-items: center;
          flex-shrink: 0;
          border-radius: 6px;
          opacity: 0;
          transition: opacity 0.15s, color 0.15s, background 0.15s;
          /* sits to the left of the sent bubble */
          order: -1;
        }

        .message-bubble-container:hover .delete-message-btn {
          opacity: 1;
        }

        .delete-message-btn:hover {
          color: #db2828 !important;
          background: rgba(219, 40, 40, 0.08);
        }

        /* Mobile: always slightly visible so user knows it exists */
        @media (max-width: 768px) {
          .delete-message-btn {
            opacity: 0.45;
            font-size: 1.15em;
          }
        }

        .scroll-anchor { height: 1px; width: 100%; visibility: hidden; }

        .scroll-to-bottom-button {
          position: absolute;
          bottom: 80px;
          right: 16px;
          z-index: 1000;
          box-shadow: 0 2px 10px rgba(0,0,0,0.2) !important;
        }

        /* ── Message input ── */
        .message-input-container {
          padding: 12px 16px;
          border-top: 1px solid #e0e0e0;
          background: white;
          flex-shrink: 0;
        }

        .message-input-container form {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .message-input {
          flex: 1;
          padding: 10px 16px;
          border: 1px solid #e0e0e0;
          border-radius: 20px;
          font-size: ${isMobile ? '16px' : '1em'};
          font-family: inherit;
          outline: none;
          transition: border-color 0.2s;
          -webkit-appearance: none;
          appearance: none;
        }

        .message-input:focus { border-color: #2185d0; }

        .send-button {
          padding: 0 !important;
          width: ${isMobile ? '40px' : '44px'};
          height: ${isMobile ? '40px' : '44px'};
          border-radius: 50% !important;
          display: flex !important;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        /* ── Desktop layout ── */
        .conversation-list-desktop {
          width: 320px;
          min-width: 320px;
          overflow: hidden;
          border-right: 1px solid #e0e0e0;
        }

        .chat-area-desktop { flex: 1; overflow: hidden; }

        /* ── Mobile ── */
        @media (max-width: 768px) {
          .messages-container-wrapper {
            margin-top: 0;
            margin-bottom: 0;
            height: 100dvh;
            padding: 0 !important;
          }

          .messages-layout { border-radius: 0; box-shadow: none; }
          .conversation-list-container { border-right: none; }

          .chat-header { padding: 10px 12px; min-height: 56px; }
          .messages-container { padding: 10px 8px; }

          .message-input-container {
            padding: 8px 10px;
            padding-bottom: max(8px, env(safe-area-inset-bottom));
          }

          .message-bubble { max-width: 80%; font-size: 0.95em; }
          .conversation-item { padding: 12px 16px; }
        }

        /* ── Scrollbars ── */
        .conversation-item:focus-visible { outline: 2px solid #2185d0; outline-offset: -2px; }
        .message-input:focus-visible { outline: 2px solid #2185d0; outline-offset: 2px; }

        .conversation-scroll-area::-webkit-scrollbar,
        .messages-container::-webkit-scrollbar { width: 6px; }

        .conversation-scroll-area::-webkit-scrollbar-track,
        .messages-container::-webkit-scrollbar-track { background: #f1f1f1; }

        .conversation-scroll-area::-webkit-scrollbar-thumb,
        .messages-container::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 3px; }

        .conversation-scroll-area::-webkit-scrollbar-thumb:hover,
        .messages-container::-webkit-scrollbar-thumb:hover { background: #a1a1a1; }
      `}</style>
    </Container>
  );
});